/**
 * 사용자 관리 페이지
 * - 사용자 목록 테이블
 * - 강사/학생 생성 모달
 * - 수정/삭제 모달
 * - 강사-학생 할당 모달
 */

import React, { useState } from 'react';
import { Card } from 'antd';
import { UserTable } from '../../components/users/UserTable';
import { CreateInstructorModal } from '../../components/users/CreateInstructorModal';
import { CreateStudentModal } from '../../components/users/CreateStudentModal';
import { EditUserModal } from '../../components/users/EditUserModal';
import { DeleteUserModal } from '../../components/users/DeleteUserModal';
import { AssignStudentsModal } from '../../components/users/AssignStudentsModal';
import { ChangeInstructorModal } from '../../components/users/ChangeInstructorModal';
import type { User } from '../../types/user';

export const UsersPage: React.FC = () => {
  // 모달 상태 관리
  const [createInstructorOpen, setCreateInstructorOpen] = useState(false);
  const [createStudentOpen, setCreateStudentOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [assignStudentsOpen, setAssignStudentsOpen] = useState(false);
  const [changeInstructorOpen, setChangeInstructorOpen] = useState(false);

  // 선택된 사용자 상태
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // 테이블 새로고침 트리거
  const [refreshKey, setRefreshKey] = useState(0);

  /**
   * 테이블 새로고침
   */
  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  /**
   * 강사 추가 모달 열기
   */
  const handleCreateInstructor = () => {
    setCreateInstructorOpen(true);
  };

  /**
   * 학생 추가 모달 열기
   */
  const handleCreateStudent = () => {
    setCreateStudentOpen(true);
  };

  /**
   * 사용자 수정 모달 열기
   */
  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setEditUserOpen(true);
  };

  /**
   * 사용자 삭제 모달 열기
   */
  const handleDelete = (user: User) => {
    setSelectedUser(user);
    setDeleteUserOpen(true);
  };

  /**
   * 학생 할당 모달 열기
   */
  const handleAssignStudents = (instructor: User) => {
    setSelectedUser(instructor);
    setAssignStudentsOpen(true);
  };

  /**
   * 담당 강사 변경 모달 열기
   */
  const handleChangeInstructor = (student: User) => {
    setSelectedUser(student);
    setChangeInstructorOpen(true);
  };

  /**
   * 모달 성공 시 처리 (테이블 새로고침)
   */
  const handleModalSuccess = () => {
    handleRefresh();
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card title="사용자 관리">
        <UserTable
          key={refreshKey}
          onCreateInstructor={handleCreateInstructor}
          onCreateStudent={handleCreateStudent}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAssignStudents={handleAssignStudents}
          onChangeInstructor={handleChangeInstructor}
        />
      </Card>

      {/* 강사 추가 모달 */}
      <CreateInstructorModal
        open={createInstructorOpen}
        onClose={() => setCreateInstructorOpen(false)}
        onSuccess={handleModalSuccess}
      />

      {/* 학생 추가 모달 */}
      <CreateStudentModal
        open={createStudentOpen}
        onClose={() => setCreateStudentOpen(false)}
        onSuccess={handleModalSuccess}
      />

      {/* 사용자 수정 모달 */}
      <EditUserModal
        open={editUserOpen}
        user={selectedUser}
        onClose={() => {
          setEditUserOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleModalSuccess}
      />

      {/* 사용자 삭제 모달 */}
      <DeleteUserModal
        open={deleteUserOpen}
        user={selectedUser}
        onClose={() => {
          setDeleteUserOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleModalSuccess}
      />

      {/* 학생 할당 모달 */}
      <AssignStudentsModal
        open={assignStudentsOpen}
        instructor={selectedUser}
        onClose={() => {
          setAssignStudentsOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleModalSuccess}
      />

      {/* 담당 강사 변경 모달 */}
      <ChangeInstructorModal
        open={changeInstructorOpen}
        student={selectedUser}
        onClose={() => {
          setChangeInstructorOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
